// ---------------------------------------------------------------------------
// Workspace route contract — whether a URL resumes memory or names a scope.
//
// Bare work surfaces are app-entry routes: they restore the workspace the user
// last visited. Explicit Personal and team routes are durable destinations for
// bookmarks, emails, and in-app switching, so they always outrank that memory.
// ---------------------------------------------------------------------------

export type WorkspaceSurface = 'projects' | 'library' | 'workflows'

export type WorkspaceDestination = { kind: 'remembered' } | { kind: 'personal' } | { kind: 'team'; slug: string }

export interface WorkspaceRoute {
  destination: WorkspaceDestination
  surface: WorkspaceSurface
}

const BARE_SURFACE = /^\/(projects|library|workflows)\/?$/
const PERSONAL_SURFACE = /^\/personal\/(projects|library|workflows)\/?$/
const TEAM_SURFACE = /^\/team\/([^/]+)\/(projects|library|workflows)\/?$/

export function parseWorkspaceRoute(pathname: string | null | undefined): WorkspaceRoute | null {
  if (!pathname) return null

  const personal = pathname.match(PERSONAL_SURFACE)
  if (personal) {
    return { destination: { kind: 'personal' }, surface: personal[1] as WorkspaceSurface }
  }

  const team = pathname.match(TEAM_SURFACE)
  if (team) {
    return {
      destination: { kind: 'team', slug: team[1] },
      surface: team[2] as WorkspaceSurface,
    }
  }

  const remembered = pathname.match(BARE_SURFACE)
  if (remembered) {
    return { destination: { kind: 'remembered' }, surface: remembered[1] as WorkspaceSurface }
  }

  return null
}

export function explicitWorkspaceHref(
  destination: Exclude<WorkspaceDestination, { kind: 'remembered' }>,
  surface: WorkspaceSurface,
): string {
  if (destination.kind === 'personal') return `/personal/${surface}`
  return `/team/${encodeURIComponent(destination.slug)}/${surface}`
}
